import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Sidebar from "../components/Sidebar";
import TrialBanner from "../components/TrialBanner";
import HardLockModal from "../components/HardLockModal";
import { TosModal, PrivacyModal } from "../components/LegalModals";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Sparkles,
  Copy,
  RefreshCw,
  ClipboardPaste,
  Wand2,
  Lightbulb,
  KeySquare,
  Chrome,
} from "lucide-react";
import { toast } from "sonner";
import EmlDownloadButton from "../components/EmlDownloadButton";
import ShareButton from "../components/ShareButton";
import ShareAnalyticsTile from "../components/ShareAnalyticsTile";
import ChromeExtensionPanel from "../components/ChromeExtensionPanel";
import ContactModal from "../components/ContactModal";

export default function Workspace() {
  const { user, refresh } = useAuth();
  const [search] = useSearchParams();

  const [roles, setRoles] = useState([]);
  const [structures, setStructures] = useState([]);
  const [samples, setSamples] = useState([]);

  const [role, setRole] = useState("A");
  const [mode, setMode] = useState("1");
  const [structure, setStructure] = useState("raw_text");

  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [genLoading, setGenLoading] = useState(false);

  const [tos, setTos] = useState(false);
  const [pri, setPri] = useState(false);
  const [hardLock, setHardLock] = useState(false);
  const [chromeOpen, setChromeOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [personaLabels, setPersonaLabels] = useState({ role: "", mode: "" });
  const analyticsRef = useRef(null);

  const textareaRef = useRef(null);
  const lastReq = useRef({ input: "", role: "A", mode: "1", structure: "raw_text" });

  // Fetch metadata
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [{ data: meta }, { data: dem }] = await Promise.all([
          api.get("/personas"),
          api.get("/demo-samples"),
        ]);
        if (!mounted) return;
        setRoles(meta.roles);
        setStructures(meta.structures);
        setSamples(dem.samples);
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Hard lock states
  useEffect(() => {
    if (!user) return;
    if (user.funnel_state === "hard_locked" || search.get("action") === "upgrade") {
      setHardLock(true);
    } else {
      setHardLock(false);
    }
  }, [user, search]);

  const currentRole = useMemo(() => roles.find((r) => r.key === role), [roles, role]);
  const modes = currentRole?.modes || [];

  useEffect(() => {
    // Reset mode when role changes
    if (currentRole && !modes.some((m) => m.key === mode)) {
      setMode(modes[0]?.key || "1");
    }
  }, [currentRole, mode, modes]);

  const callGenerate = useCallback(async (overrides = {}) => {
    const payload = {
      input_text: overrides.input ?? input,
      role: overrides.role ?? role,
      mode: overrides.mode ?? mode,
      structure: overrides.structure ?? structure,
    };
    if (!payload.input_text.trim()) {
      toast.error("Paste something to transform first.");
      return;
    }
    try {
      setGenLoading(true);
      setOutput("");
      lastReq.current = payload;
      const { data } = await api.post("/generate", payload);
      setOutput(data.output);
      setPersonaLabels({ role: data.role_label || "", mode: data.persona_label || "" });
      await refresh();
      if (data.remaining_today !== null && data.remaining_today !== undefined) {
        toast(`${data.remaining_today} generation${data.remaining_today === 1 ? "" : "s"} left today (trial)`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "Generation failed";
      if (e.response?.status === 402) {
        setHardLock(true);
      }
      toast.error(detail);
    } finally {
      setGenLoading(false);
    }
  }, [input, role, mode, structure, refresh]);

  // Magic Paste: Ctrl+V / Cmd+V when not in input field
  useEffect(() => {
    const handler = async (e) => {
      const isV = (e.ctrlKey || e.metaKey) && (e.key === "v" || e.key === "V");
      if (!isV) return;
      const tag = (document.activeElement?.tagName || "").toLowerCase();
      const editable = document.activeElement?.isContentEditable;
      if (tag === "input" || tag === "textarea" || editable) return; // let native paste happen
      try {
        const text = await navigator.clipboard.readText();
        if (text && text.trim()) {
          e.preventDefault();
          setInput(text);
          toast.success("Magic Paste: input filled. Generating…");
          // Defer the generate so input state propagates
          setTimeout(() => callGenerate({ input: text }), 50);
        }
      } catch {
        // clipboard permission denied — silent
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [callGenerate]);

  const loadSample = (s) => {
    setInput(s.text);
    setRole(s.role);
    setMode(s.mode);
    toast(`Loaded sample: ${s.label}`);
  };

  const copyOutput = async () => {
    if (!output) return;
    await navigator.clipboard.writeText(output);
    toast.success("Copied to clipboard");
  };

  const regenerate = () => {
    if (lastReq.current.input_text || lastReq.current.input) {
      callGenerate(lastReq.current);
    } else {
      callGenerate();
    }
  };

  return (
    <div className="min-h-screen flex bg-obsidian text-white">
      <Sidebar onOpenTos={() => setTos(true)} onOpenPrivacy={() => setPri(true)} onOpenContact={() => setContactOpen(true)} onOpenChrome={() => setChromeOpen(true)} />

      <main className="flex-1 min-w-0 flex flex-col">
        <TrialBanner onUpgrade={() => setHardLock(true)} />

        {/* Top bar */}
        <div className="border-b border-border bg-charcoal/40 backdrop-blur-md">
          <div className="px-6 lg:px-10 py-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/40">Workspace</div>
              <h1 className="font-display text-2xl text-white tracking-tight">S.A.I. Control Room</h1>
            </div>
            <div className="flex items-center gap-3">
              {samples.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      data-testid="demo-samples-trigger"
                      variant="outline"
                      className="border-border bg-charcoal hover:bg-white/[0.04] text-white rounded-sm"
                    >
                      <Lightbulb className="h-4 w-4 mr-2 text-teal" /> Demo Sample
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-[420px] bg-charcoal border-border">
                    <DropdownMenuLabel className="text-white/60 text-[11px] font-mono uppercase tracking-[0.16em]">Pick a high-stakes scenario</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {samples.map((s, i) => (
                      <DropdownMenuItem
                        key={i}
                        data-testid={`demo-sample-${i}`}
                        onClick={() => loadSample(s)}
                        className="cursor-pointer text-white/80 hover:text-white focus:bg-white/[0.05] focus:text-white"
                      >
                        <Sparkles className="h-3.5 w-3.5 mr-2 text-teal/80" />
                        <span className="truncate">{s.label}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Button
                data-testid="open-chrome-ext-btn"
                variant="outline"
                onClick={() => setChromeOpen(true)}
                className="border-border bg-charcoal hover:bg-white/[0.04] text-white rounded-sm"
              >
                <Chrome className="h-4 w-4 mr-2 text-teal" /> Chrome Extension
              </Button>
            </div>
          </div>
        </div>

        {/* Split-screen */}
        <div className="flex-1 grid lg:grid-cols-2 gap-px bg-border min-h-0">
          {/* LEFT: Input panel */}
          <section className="bg-obsidian p-6 lg:p-8 flex flex-col min-h-0" data-testid="input-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="sr-pill text-teal">
                <span className="h-1.5 w-1.5 bg-teal rounded-full" /> Input
              </div>
              <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-white/40">
                {input.length} chars
              </span>
            </div>

            {/* Persona + Mode selectors */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[10px] font-mono uppercase tracking-[0.16em] text-white/40 block mb-1.5">Persona Profile</label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="persona-role-select" className="bg-charcoal border-border h-10 rounded-sm">
                    <SelectValue placeholder="Pick persona" />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-border">
                    {roles.map((r) => (
                      <SelectItem key={r.key} value={r.key} data-testid={`persona-role-${r.key}`}>
                        <span className="font-mono text-xs text-teal mr-2">{r.key}</span>{r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-[0.16em] text-white/40 block mb-1.5">Context Mode</label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger data-testid="persona-mode-select" className="bg-charcoal border-border h-10 rounded-sm">
                    <SelectValue placeholder="Pick mode" />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-border">
                    {modes.map((m) => (
                      <SelectItem key={m.key} value={m.key} data-testid={`persona-mode-${m.key}`}>
                        <span className="font-mono text-xs text-teal/80 mr-2">M{m.key}</span>{m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Textarea
              ref={textareaRef}
              data-testid="input-textarea"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Paste the incoming email, ticket, Slack thread, or rough draft here…"
              className="flex-1 min-h-[280px] bg-charcoal border-border resize-none text-white placeholder:text-white/30 font-mono text-[13px] leading-relaxed rounded-sm focus-visible:ring-teal"
            />

            <div className="mt-4 flex items-center justify-between gap-3">
              <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-white/40 flex items-center gap-2">
                <ClipboardPaste className="h-3.5 w-3.5" /> Magic Paste: hit ⌘/Ctrl+V outside the field
              </div>
              <Button
                data-testid="generate-btn"
                onClick={() => callGenerate()}
                disabled={genLoading || !input.trim()}
                className="bg-teal hover:bg-teal-hover text-white rounded-sm h-10 px-5"
              >
                {genLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Generating…
                  </>
                ) : (
                  <>
                    <Wand2 className="h-4 w-4 mr-2" /> Generate Response
                  </>
                )}
              </Button>
            </div>
          </section>

          {/* RIGHT: Output panel */}
          <section className="bg-obsidian p-6 lg:p-8 flex flex-col min-h-0" data-testid="output-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="sr-pill text-teal">
                <span className={`h-1.5 w-1.5 bg-teal rounded-full ${genLoading ? "sr-blink" : ""}`} /> AI Response
              </div>
              {output && (
                <div className="flex items-center gap-2">
                  <EmlDownloadButton
                    output={output}
                    fromName={user?.name}
                    fromEmail={user?.email}
                    personaLabel={personaLabels.role}
                    modeLabel={personaLabels.mode}
                    structure={structure}
                  />
                  <ShareButton output={output} />
                  <Button
                    data-testid="copy-output-btn"
                    size="sm"
                    variant="outline"
                    onClick={copyOutput}
                    className="h-8 border-border bg-charcoal hover:bg-white/[0.04] text-white/80 rounded-sm"
                  >
                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                  </Button>
                  <Button
                    data-testid="regenerate-btn"
                    size="sm"
                    variant="outline"
                    onClick={regenerate}
                    disabled={genLoading}
                    className="h-8 border-border bg-charcoal hover:bg-white/[0.04] text-white/80 rounded-sm"
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Regenerate
                  </Button>
                </div>
              )}
            </div>

            {/* Structure Tweak */}
            <Tabs value={structure} onValueChange={(v) => { setStructure(v); if (output) callGenerate({ structure: v }); }} className="mb-3">
              <TabsList className="bg-charcoal border border-border rounded-sm h-9 p-0.5">
                {structures.map((s) => (
                  <TabsTrigger
                    key={s.key}
                    value={s.key}
                    data-testid={`structure-${s.key}`}
                    className="data-[state=active]:bg-teal data-[state=active]:text-white text-white/60 rounded-sm text-xs h-8 px-3"
                  >
                    {s.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            <div
              data-testid="output-content"
              className="flex-1 min-h-[280px] sr-card sr-grain p-5 sr-scroll overflow-y-auto whitespace-pre-wrap text-[14px] leading-relaxed text-white/90 rounded-sm sr-fade-in"
            >
              {genLoading && !output && (
                <div className="text-white/40 font-mono text-xs sr-blink">Drafting calm, grounded response…</div>
              )}
              {!genLoading && !output && (
                <div className="text-white/30 text-sm">
                  Your AI response will appear here. Pick a persona, paste your context on the left, and press Generate.
                </div>
              )}
              {output}
            </div>

            {user?.api_key && (
              <details className="mt-4 text-xs text-white/40">
                <summary className="cursor-pointer flex items-center gap-2 hover:text-white/70">
                  <KeySquare className="h-3 w-3" /> API key (Chrome extension ready)
                </summary>
                <code
                  data-testid="api-key-value"
                  className="block mt-2 p-2 bg-charcoal border border-border rounded-sm font-mono text-[11px] text-teal break-all"
                >
                  {user.api_key}
                </code>
              </details>
            )}

            <ShareAnalyticsTile ref={analyticsRef} />
          </section>
        </div>
      </main>

      <HardLockModal
        open={hardLock}
        allowDismiss={user?.funnel_state !== "hard_locked"}
        onClose={() => setHardLock(false)}
      />
      <ChromeExtensionPanel open={chromeOpen} onOpenChange={setChromeOpen} />
      <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
      <TosModal open={tos} onOpenChange={setTos} />
      <PrivacyModal open={pri} onOpenChange={setPri} />
    </div>
  );
}
